import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {tap} from 'rxjs/operators'
import {BehaviorSubject, Observable, Subject, throwError} from 'rxjs';
import {jwtDecode} from "jwt-decode";
import {Router} from "@angular/router";
import {RbacService} from "./rbac.service";
import {environment} from "../../../environment/environment";

/** A real session that went bad — an admin changed the user's role/permissions/module access. */
export const ACCESS_CHANGED_MESSAGE = 'Your access has changed. Please sign in again.';
/** Generic fallback: the token exists but can't be trusted (expired, malformed, refresh failed). */
export const SESSION_INVALID_MESSAGE = 'Your session is no longer valid. Please sign in again.';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  /** Fires whenever a fresh access token is stored — initial login or a silent
   *  refresh — so anything holding decoded identity/balance (header, header-mobile)
   *  knows to re-read it instead of keeping whatever it captured at its own init. */
  private identityChangedSubject = new Subject<void>();
  identityChanged$ = this.identityChangedSubject.asObservable();

  private readonly rbacBaseUrl = (environment as any).rbac_base_url;

  constructor(private http: HttpClient, private router: Router, private rbac: RbacService) {
  }

  private readonly TOKEN_KEY = 'auth_token';

  private readonly REFRESH_TOKEN_KEY = 'auth_refresh_token';

  private readonly SESSION_MESSAGE_KEY = 'auth_session_message';

  getAndClearSessionMessage(): string | null {
    const message = sessionStorage.getItem(this.SESSION_MESSAGE_KEY);
    if (message) {
      sessionStorage.removeItem(this.SESSION_MESSAGE_KEY);
    }
    return message;
  }

  setRefreshToken(token: any): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  removeRefreshToken(): void {
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  setToken(token: any): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    this.identityChangedSubject.next();
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const isauth = !!token && this.decodeToken(token) !== null;
    this.isAuthenticatedSubject.next(isauth)
    return isauth
  }

  /**
   * Guard helper: true if genuinely authenticated. If not, logs out with a message —
   * but only when there was a token to begin with (a real session gone bad, e.g. a
   * corrupted or expired one). A plain, never-logged-in visitor hitting a protected
   * route gets a silent redirect to login, which is the correct, expected UX there.
   */
  requireAuthenticated(): boolean {
    if (this.isAuthenticated()) {
      return true;
    }
    if (this.getToken()) {
      this.logout(SESSION_INVALID_MESSAGE);
    } else {
      this.router.navigate(['/accounts']);
    }
    return false;
  }

  /** A 401 whose body carries `code: "session_invalidated"` means the backend deliberately
   *  killed this session (e.g. a role/permission/module change) — the interceptor has
   *  already logged out with the right message. Anything else falls back to wherever the
   *  caller wants to send an authenticated-but-unauthorized user. */
  handleRbacFetchError(error: any, fallbackUrl: string[] = ['/booking']): void {
    if (error?.status === 401) {
      return;
    }
    this.router.navigate(fallbackUrl);
  }

  private decodeToken(token: string): any | null {
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }

  login(email: string, password: string): Observable<{ access: string; refresh: string }> {
    return this.http.post<{ access: string; refresh: string }>(`${this.rbacBaseUrl}/token/`, { email, password });
  }

  refreshAccessToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }
    return this.http.post<{ access: string }>(`${this.rbacBaseUrl}/token/refresh/`, { refresh: refreshToken }).pipe(
      tap((data: any) => this.setToken(data.access))
    );
  }

  user(): any {
    const token = this.getToken();
    const decoded = token ? this.decodeToken(token) : null;
    if (!decoded) {
      // A malformed/corrupted token would otherwise throw here, aborting whatever
      // component ngOnInit called this mid-way through and leaving a half-broken page
      // with no explanation. Log out with a message instead.
      if (token) {
        this.logout(SESSION_INVALID_MESSAGE);
      }
      return {};
    }
    return decoded;
  }

  getUserCompanyId(): string {
    const user = this.user()
    return user.companny_id
  }

  /** Doesn't navigate itself — AppComponent's isAuthenticatedSubject subscription redirects
   *  to /accounts, but only when the current route isn't one of the public routes (e.g.
   *  /activate, /reset-password) that must stay reachable signed-out. A direct navigate here
   *  would hijack a visitor away from an activation/reset link whenever a stale token in
   *  localStorage triggers a background 401 on app load. */
  logout(message?: string) {
    this.removeToken()
    this.removeRefreshToken()
    this.rbac.clear()
    if (message) {
      sessionStorage.setItem(this.SESSION_MESSAGE_KEY, message);
    }
    this.isAuthenticatedSubject.next(false)
  }

  isApprover(): boolean {
    return this.user().is_approver
  }

  isOrgAdmin(): boolean {
    // TODO - perhaps this is not the best role to define an ORG Admin
    // currently all approvers got the same role
    return this.user().is_admin
  }
  isSuperAdmin(): boolean {
    return this.user().is_superuser
  }
}
